'use client';

import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import Breadcrumbs from '@/components/ui/breadcrumbs';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const fadeLeft = {
  initial: { opacity: 0, x: -40 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const fadeRight = {
  initial: { opacity: 0, x: 40 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const milestones = [
  {
    year: '2020',
    title: 'The Beginning',
    description:
      'Cristian and Liliana launch their online clothing printing and embroidery business with a passion for fashion and a whole lot of hustle.',
  },
  {
    year: '2023',
    title: 'Birmingham Shop Opens',
    description:
      'A dream materialised — the perfect brick-and-mortar shop in Birmingham. The business boomed, growing by over 10 times.',
  },
  {
    year: '2023',
    title: 'Trademarked & Award-Winning',
    description:
      'Successfully trademarked the OK T-Shirt logo and awarded "Best Custom Printed T-Shirt Supplier in the West Midlands, UK."',
  },
  {
    year: '2023',
    title: 'Embroidery Expansion',
    description:
      'Bought the first embroidery machine, targeting schools and businesses wanting a more professional look on their workwear.',
  },
  {
    year: '2025',
    title: 'Growing Demand',
    description:
      'Acquired a second embroidery machine for caps and beanies due to growing demand from clients.',
  },
  {
    year: '2026',
    title: 'Banner Printing & Roland TrueVIS',
    description:
      'Diversified into banner printing with a Roland TrueVIS 8-colour printer — helping businesses get noticed through large-format printing.',
  },
];

const achievements = [
  {
    title: 'Award-Winning',
    description: 'Best Custom Printed T-Shirt Supplier in the West Midlands, UK (2023)',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.003 6.003 0 01-4.52-1.978 6.003 6.003 0 01-4.52 1.978" />,
  },
  {
    title: 'Trademarked Brand',
    description: 'Successfully trademarked in 2023 — a respected name in the personalisation game.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />,
  },
  {
    title: '10x Growth',
    description: 'Business grew over 10 times after opening the Birmingham shop in 2023.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />,
  },
  {
    title: 'Proud Sponsor — Buxton Speedway',
    description: 'Supporting the returning Buxton Bulls with coordinated teamwear and supporter merchandise.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.58-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />,
  },
];

const whoWeServe = [
  {
    title: 'Businesses',
    description: 'Uniforms, branded merchandise, and promotional workwear for companies of all sizes.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />,
  },
  {
    title: 'Schools',
    description: 'School uniforms, PE kits, sweatshirts, cardigans, and book bags with custom embroidery.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.636 50.636 0 00-2.658-.813A59.906 59.906 0 0112 3.493a59.903 59.903 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />,
  },
  {
    title: 'Sports Teams',
    description: 'Team kits, training wear, and supporter merchandise with custom printing and embroidery.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.003 6.003 0 01-4.52-1.978 6.003 6.003 0 01-4.52 1.978" />,
  },
  {
    title: 'Families & Individuals',
    description: 'Unique pieces for birthdays, baby showers, weddings, graduations, stag and hen parties, and more.',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />,
  },
];

const statCards = [
  { value: '10x', label: 'Business growth after opening the Birmingham shop' },
  { value: '2020', label: 'Founded by Cristian & Liliana' },
  { value: 'UK-Wide', label: 'Shipping personalised garments across the country' },
  { value: '5 Stars', label: 'Google-rated for quality and fast turnaround' },
];

const growthItems = [
  { num: '01', title: 'Trusted by Businesses', desc: 'Contracts with schools, partnerships with local companies, and branded workwear for teams of every size.' },
  { num: '02', title: 'Former Sponsor — Birmingham Speedway', desc: 'A historic partnership with the Perry Barr club, which closed after the 2025 season when the stadium was lost.' },
  { num: '03', title: 'Personal Milestones, Made Unique', desc: 'Weddings, birthdays, baby showers, graduations, hen parties — they create pieces that make every moment memorable.' },
];

export default function AboutPage() {
  return (
    <div>
      <Breadcrumbs items={[{ label: 'About Us' }]} />
      {/* Hero Section — no scroll animation (visible on load) */}
      <section className="py-16 lg:py-24 bg-black">
        <div className="container">
          <motion.div
            className="max-w-[48rem] mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-4">
              Est. 2020 — Birmingham, UK
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
              Our Story
            </h1>
            <p className="text-lg lg:text-xl text-gray-300 leading-relaxed">
              From an online startup to Birmingham&apos;s award-winning custom printing and embroidery brand — built by Cristian and Liliana with passion, hard work, and a love for creating unique pieces that tell your story.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section — The Beginning */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div {...fadeLeft}>
              <p className="text-sm font-semibold uppercase tracking-widest text-[var(--color-text-muted)] mb-3">
                Where It All Began
              </p>
              <h2 className="text-2xl lg:text-3xl font-bold mb-6">
                A Husband-and-Wife Team with a Big Dream
              </h2>
              <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4">
                In 2020, Cristian and Liliana turned a shared passion for fashion into a business — launching an online custom printing and embroidery service from scratch. For three years, they built it the hard way: one order at a time, one happy customer at a time.
              </p>
              <p className="text-[var(--color-text-secondary)] leading-relaxed">
                By 2023, the online shop had outgrown the digital world. They opened a brick-and-mortar print shop in Great Barr, Birmingham — and the business grew over 10 times almost overnight.
              </p>
            </motion.div>
            <div className="grid grid-cols-2 gap-4">
              {statCards.map((card, i) => (
                <motion.div
                  key={card.value}
                  className="p-6 bg-[var(--color-off-white)] border border-[var(--color-border)]"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, ease: 'easeOut', delay: i * 0.1 }}
                >
                  <p className="text-3xl font-bold mb-1">{card.value}</p>
                  <p className="text-sm text-[var(--color-text-secondary)]">{card.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Story Section — Growth & Trust */}
      <section className="py-16 lg:py-24 bg-[var(--color-warm-white)]">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="space-y-6">
                {growthItems.map((item, i) => (
                  <motion.div
                    key={item.num}
                    className="flex gap-4"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, ease: 'easeOut', delay: i * 0.15 }}
                  >
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-black text-white font-bold text-sm">{item.num}</div>
                    <div>
                      <h3 className="font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            <motion.div className="order-1 lg:order-2" {...fadeRight}>
              <p className="text-sm font-semibold uppercase tracking-widest text-[var(--color-text-muted)] mb-3">
                More Than a Print Shop
              </p>
              <h2 className="text-2xl lg:text-3xl font-bold mb-6">
                Built on Quality, Driven by Community
              </h2>
              <p className="text-[var(--color-text-secondary)] leading-relaxed">
                What started as an online venture quickly became a brand that Birmingham trusts. From school uniforms to corporate workwear to one-of-a-kind celebration pieces — OK T-Shirt earned its reputation through fast turnaround, consistent quality, and genuine care for every order.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-16 lg:py-24 bg-[var(--color-off-white)]">
        <div className="container">
          <motion.h2
            className="text-2xl lg:text-3xl font-bold text-center mb-12"
            {...fadeUp}
          >
            Achievements &amp; Recognition
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-[64rem] mx-auto">
            {achievements.map((item, i) => (
              <motion.div
                key={item.title}
                className="text-center p-8 bg-white border border-[var(--color-border)]"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: i * 0.1 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-[var(--color-off-white)]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                    {item.icon}
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-[var(--color-text-secondary)]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Speedway partnerships — current and historical */}
      <section id="speedway-partnerships" className="scroll-mt-24 py-16 lg:py-24 bg-black text-white">
        <div className="container">
          <motion.div className="max-w-[46rem]" {...fadeUp}>
            <p className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-3">
              Speedway partnerships
            </p>
            <h2 className="text-2xl lg:text-3xl font-bold mb-5 text-white">
              Supporting the sport, with the story kept accurate
            </h2>
            <p className="text-gray-300 leading-relaxed">
              Our speedway work spans a former Birmingham partnership and a current relationship with Buxton Speedway. The distinction matters: one is part of our local history, while the other is an active club partnership we are proud to support today.
            </p>
          </motion.div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <motion.div
              className="border border-white/20 bg-white/5 p-7 lg:p-9"
              {...fadeLeft}
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">Historical partnership</p>
              <h3 className="mt-3 text-xl font-bold text-white">Former Official Sponsor — Birmingham Speedway</h3>
              <p className="mt-4 text-sm leading-7 text-gray-300">
                OK T-Shirt previously supported Birmingham Speedway at Perry Barr. The club closed after the 2025 season following the loss of the stadium, so this partnership is now remembered as part of the company&apos;s history rather than presented as current.
              </p>
            </motion.div>
            <motion.div
              className="border border-white/20 bg-white p-7 text-black lg:p-9"
              {...fadeRight}
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Current partnership</p>
              <h3 className="mt-3 text-xl font-bold">Proud Sponsor — Buxton Speedway</h3>
              <p className="mt-4 text-sm leading-7 text-gray-700">
                We currently support the returning Buxton Bulls with embroidered teamwear, printed supporter clothing and coordinated club merchandise for riders, staff and fans.
              </p>
              <Link
                href="/buxton-speedway-teamwear-merchandise"
                className="mt-6 inline-flex items-center text-sm font-semibold text-black underline decoration-black/30 underline-offset-4 hover:decoration-black"
              >
                Read the Buxton Speedway case study
                <span aria-hidden="true" className="ml-2">→</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-12 lg:gap-16">
            {/* Sticky heading column */}
            <div className="lg:sticky lg:top-32 lg:self-start">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-[var(--color-text-muted)] mb-3">
                  From 2020 to Today
                </p>
                <h2 className="text-2xl lg:text-3xl font-bold mb-4">
                  Our Journey
                </h2>
                <p className="text-[var(--color-text-secondary)] leading-relaxed hidden lg:block">
                  Every milestone shaped who we are. From a kitchen-table startup to an award-winning Birmingham brand.
                </p>
              </motion.div>
            </div>

            {/* Timeline column */}
            <div>
              <div className="space-y-0">
                {milestones.map((milestone, index) => (
                  <motion.div
                    key={index}
                    className="relative pl-8 pb-12 last:pb-0 border-l-2 border-[var(--color-border)] last:border-l-0"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, ease: 'easeOut', delay: index * 0.08 }}
                  >
                    <div className="absolute -left-[9px] top-0 w-4 h-4 bg-black border-2 border-black" />
                    <p className="text-sm font-bold uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
                      {milestone.year}
                    </p>
                    <h3 className="text-lg font-semibold mb-2">{milestone.title}</h3>
                    <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                      {milestone.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-16 lg:py-24 bg-[var(--color-off-white)]">
        <div className="container">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl lg:text-3xl font-bold text-center mb-4">
              Who We Serve
            </h2>
            <p className="text-center text-[var(--color-text-secondary)] mb-12 max-w-[36rem] mx-auto">
              From multinational businesses to families celebrating milestones — we create personalised clothing for everyone.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whoWeServe.map((item, i) => (
              <motion.div
                key={item.title}
                className="text-center p-8 bg-white border border-[var(--color-border)]"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: i * 0.1 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-[var(--color-off-white)]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                    {item.icon}
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-[var(--color-text-secondary)]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team Section */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl lg:text-3xl font-bold text-center mb-4">
              Meet the Faces Behind Our Success
            </h2>
            <p className="text-center text-[var(--color-text-secondary)] mb-12 max-w-[36rem] mx-auto">
              The people who make OK T-Shirt what it is today.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8 max-w-[56rem] mx-auto">
            {[
              {
                name: 'Liliana',
                role: 'CEO',
                image: '/images/team/liliana.webp',
                alt: 'Liliana, CEO of OK T-Shirt',
                description: 'With 5 years of experience in the fashion field, I set out to offer my skill in this environment by offering a product so necessary to any person, family, child.',
              },
              {
                name: 'Cristian',
                role: 'CEO',
                image: '/images/team/cristian.webp',
                alt: 'Cristian, CEO of OK T-Shirt',
                description: 'With more than 15 years of experience in the IT and financial fields, we offer our clients the most pleasant experience when it comes to personalized t-shirts.',
              },
              {
                name: 'Andreea-Popescu',
                role: 'Marketing Expert',
                image: '/images/team/andreea.webp',
                alt: 'Andreea-Popescu, Marketing Expert at OK T-Shirt',
                description: "Student at the University of Marketing in Lyon, France, I'm fulfilling my duties in one of the most beautiful fields online — Digital Marketing.",
              },
            ].map((member, i) => (
              <motion.div
                key={member.name}
                className="text-center p-8 bg-[var(--color-off-white)] border border-[var(--color-border)]"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: i * 0.15 }}
              >
                <div className="w-32 h-32 mx-auto mb-5 overflow-hidden rounded-full">
                  <Image
                    src={member.image}
                    alt={member.alt}
                    width={128}
                    height={128}
                    sizes="128px"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-semibold">{member.name}</h3>
                <p className="text-sm font-medium text-[var(--color-text-muted)] uppercase tracking-wider mb-3">{member.role}</p>
                <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <motion.div
            className="max-w-[48rem] mx-auto text-center"
            {...fadeUp}
          >
            <h2 className="text-2xl lg:text-3xl font-bold mb-6">Our Vision</h2>
            <p className="text-lg text-[var(--color-text-secondary)] leading-relaxed">
              To become Birmingham&apos;s one-stop shop for all things printing and embroidery — offering exceptional personalisation services to businesses of all sizes. This isn&apos;t just a business; it&apos;s a story woven with passion, hard work, and a love for creating unique pieces that tell your story.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-black">
        <div className="container">
          <motion.div
            className="max-w-[64rem] mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
              Ready to Create
              <br className="hidden sm:block" />
              <span className="sm:hidden"> </span>
              Something Unique?
            </h2>
            <p className="text-lg lg:text-xl text-gray-300 mb-10 leading-relaxed">
              Whether you need custom workwear, school uniforms, or personalised gifts — we&apos;d love to hear from you.
            </p>
            <Link
              href="/contact"
              className="group inline-flex items-center justify-center gap-2 px-10 py-4 text-base font-semibold bg-white text-black hover:bg-gray-100 transition-all duration-300"
            >
              Get in Touch
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
